import { useEffect, useState } from "react";
import { LuX } from "react-icons/lu";
import { createPortal } from "react-dom";

interface TModal {
  open: boolean;
  handleClose: () => void;
  className?: string;
  children: React.ReactNode;
  hasOverlay?: boolean;
  title?: string;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  showCloseButton?: boolean;
  footer?: React.ReactNode;
  headerActions?: React.ReactNode;
  disableClose?: boolean;
}

const Modal = ({
  open,
  handleClose,
  children,
  className,
  hasOverlay = false,
  title,
  size = "md",
  showCloseButton = true,
  footer,
  headerActions,
  disableClose = false,
}: TModal) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // double rAF ensures the element is in the DOM before the transition kicks in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const sizeClasses = {
    sm: "max-w-md w-full",
    md: "max-w-2xl w-full",
    lg: "max-w-4xl w-full",
    xl: "max-w-6xl w-full",
    full: "w-[95vw] h-[95vh]",
  };

  const onClose = () => {
    if (!disableClose) handleClose();
  };

  const hasHeader = Boolean(title || headerActions);
  const showFloatingClose = !hasHeader && showCloseButton && !disableClose;
  const showHeaderClose = hasHeader && showCloseButton && !disableClose;

  if (!mounted) return null;

  const modalContent = (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transition: "opacity 200ms ease-in-out",
      }}
      className={`fixed inset-0 w-screen h-screen flex items-center justify-center z-[100] p-4 ${
        hasOverlay
          ? "bg-gradient-to-br from-blue-50/60 via-purple-50/60 to-pink-50/60 backdrop-blur-md"
          : "bg-black/60 backdrop-blur-sm"
      } ${disableClose ? "cursor-not-allowed" : "cursor-default"}`}
      onClick={onClose}
    >
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1) translateY(0px)" : "scale(0.95) translateY(20px)",
          transition:
            "opacity 300ms cubic-bezier(0.4,0,0.2,1), transform 300ms cubic-bezier(0.4,0,0.2,1)",
        }}
        className={`bg-white relative rounded-2xl shadow-2xl ${sizeClasses[size]} ${
          className || ""
        } max-h-[90vh] flex flex-col overflow-hidden scrollbar-none cursor-default`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {hasHeader && (
          <div className="flex items-center justify-between gap-4 px-6 py-5 border-b border-gray-100">
            {title && (
              <h2 className="text-xl font-bold text-gray-900 flex-1">{title}</h2>
            )}
            <div className="flex items-center gap-2">
              {headerActions}
              {showHeaderClose && (
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors duration-200 group"
                >
                  <LuX size={20} className="text-gray-500 group-hover:text-gray-700" />
                </button>
              )}
            </div>
          </div>
        )}

        {showFloatingClose && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/90 hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl group"
          >
            <LuX size={20} className="text-gray-500 group-hover:text-gray-700" />
          </button>
        )}

        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 scrollbar-none">
          {children}
        </div>

        {footer && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof window !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  return null;
};

export default Modal;
